import React from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';

import {
  CreateUpdateExamDto,
  GenerateReceiptDtoPaymentMethodEnum,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import FormRow from '../../../app/components/FormRow';
import ValidationAlert from '../../../app/components/ValidationAlert';
import { ExamEntity } from '../../../common/spdCore/contexts/ReportContext';
import { PaymentFormEnum } from '../../../common/spdCore/validation/enums';
import { PaymentFormSelector } from '../../components/SalesInvoice/PaymentFormSelector';

import { ReceiptItemsList } from './ReceiptItemsList';
import { CashPaymentDetails } from './CashPaymentDetails';
import { useReceiptForm } from './useReceiptForm';

interface ExamReceiptEditorProps {
  exam: ExamEntity;
  onCompleted?: (exam: CreateUpdateExamDto) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const ExamReceiptEditor: React.FC<ExamReceiptEditorProps> = ({
  exam,
  onCompleted,
}) => {
  const {
    receiptData,
    setReceiptData,
    showSaveErrors,
    saveErrors,
    showSuccess,
    successMessage,
    total,
    isButtonDisabled,
    handlePaymentMethodChange,
    handleItemChange,
    addReceiptItem,
    removeReceiptItem,
    handleSave,
    handleSaveEFiscal,
    isLoading,
  } = useReceiptForm({ exam, onCompleted });

  return (
    <div className="mb-3">
      <Card className="exam-receipt-card">
        <Card.Header>
          <Card.Title className="mb-0">Paragon fiskalny</Card.Title>
        </Card.Header>
        <Card.Body>
          <Form className="d-grid gap-3">
            <ReceiptItemsList
              items={receiptData.items}
              onItemChange={handleItemChange}
              onAddItem={addReceiptItem}
              onRemoveItem={removeReceiptItem}
            />
            <FormRow controlId="paymentMethod" label="Forma płatności">
              <PaymentFormSelector
                value={receiptData.paymentMethod as unknown as PaymentFormEnum}
                onChange={v =>
                  handlePaymentMethodChange(
                    v as unknown as GenerateReceiptDtoPaymentMethodEnum,
                  )
                }
              />
            </FormRow>
            <CashPaymentDetails
              total={total}
              paid={receiptData.paid || ''}
              onPaidChange={paid => setReceiptData({ ...receiptData, paid })}
            />
          </Form>
          {showSaveErrors && (
            <ValidationAlert
              errors={saveErrors}
              show={showSaveErrors}
              className="mb-3 mt-3"
            />
          )}
          {showSuccess && (
            <Alert variant="success" className="mb-3 mt-3">
              {successMessage}
            </Alert>
          )}
          <div className="d-flex gap-2 mt-3">
            <Button
              onClick={handleSave}
              className="flex-fill"
              disabled={isButtonDisabled}
            >
              {isLoading ? 'Wysyłanie...' : 'Wyślij i wydrukuj paragon'}
            </Button>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="efiscal-email-tooltip">
                  E-paragon wymaga adresu e-mail badanego.
                </Tooltip>
              }
              show={!exam.examined?.email ? undefined : false}
            >
              <span className="flex-fill d-inline-block">
                <Button
                  variant="outline-primary"
                  onClick={handleSaveEFiscal}
                  className="w-100"
                  disabled={isButtonDisabled || !exam.examined?.email}
                >
                  {isLoading ? 'Wysyłanie...' : 'Wyślij e-paragon'}
                </Button>
              </span>
            </OverlayTrigger>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};
